import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type {
  DataViewStatus,
  PaginationActionType,
  SortCallbackParams,
  StatusAction as DataViewStatusAction,
} from '@vtex/admin-ui'
import {
  csx,
  DataView,
  DataViewHeader,
  Flex,
  get,
  Pagination,
  Table,
  TBody,
  TBodyCell,
  TBodyRow,
  THead,
  THeadCell,
  useDataViewState,
  usePaginationState,
  useTableState,
} from '@vtex/admin-ui'
import { PaginationRangeLabel } from '@/components/PaginationRangeLabel/PaginationRangeLabel'
import {
  type DownloadItemProps,
  DownloadListCollapser,
} from '@/components/products-listing/components/DownloadCollapser/DownloadListCollapser'
import { ImportAlerts } from '@/components/products-listing/components/ImportAlerts/ImportAlerts'
import {
  ProductSearch,
  useProductSearch,
} from '@/components/products-listing/components/ProductSearch'
import { useProductImport } from '@/components/products-listing/hooks/useImportRequest'
import { InactiveItemModalProvider } from '@/components/products-listing/InactiveItemModal'
import { ProductSKUsOffersModalProvider } from '@/components/products-listing/ProductSKUsOffersModal'
import { transformProductListFiltersToEventProperties } from '@/contexts/analytics/lib/transformProductListFilters'
import { ProductOffersProvider } from '@/contexts/productOffers'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useProducts } from '@/hooks/useProducts'
import { useSortPagination } from '@/hooks/useSortPagination'
import { type ProductOffer } from '@/pages/api/[account]/search/offers/ProductOffer'
import { type Product } from '@/types/product'

import { WithFeatureFlag } from '../feature-flag/with-feature-flag'
import FilterGroup from '../filters/FilterGroup/FilterGroup'

import { ExportMenu } from './components/ExportMenu/ExportMenu'
import { ImportMenu, type ImportType } from './components/ImportMenu/ImportMenu'
import {
  SkuListProvider,
  SKUListShortcutProvider,
} from './components/SKUList/contexts'
import {
  ColumnsIDs,
  useProductListTableColumns,
} from './hooks/useProductListTableColumns'
import { SkuList } from './components'
import {
  FilterBrand,
  FilterCategory,
  FilterCreatedAt,
  FilterKit,
  FilterSalesChannel,
  FilterSeller,
  FilterStatus,
  FilterVisible,
  type SerializedFiltersData,
} from './filters'

import * as style from './products-listing.styles'

const PAGE_SIZE = 25

type FilterGroupState = {
  adminCatalogStatusFilter: SerializedFiltersData['FilterStatus']
  adminCatalogBrandFilter: SerializedFiltersData['FilterBrand']
  adminSellerFilter: SerializedFiltersData['FilterSeller']
  adminCatalogKitFilter: SerializedFiltersData['FilterKit']
  adminCatalogVisibleFilter: SerializedFiltersData['FilterVisible']
  adminSalesChannelFilter: SerializedFiltersData['FilterSalesChannel']
  adminCreatedAtFilter: SerializedFiltersData['FilterCreatedAt']
  adminCatalogCategoryV2Filter: SerializedFiltersData['FilterCategory']
}

export function ProductsListing() {
  const { formatMessage } = useIntl()

  const [filters, setFilters] = useState<FilterGroupState>({
    adminCatalogStatusFilter: '',
    adminCatalogBrandFilter: [],
    adminSellerFilter: [],
    adminCatalogKitFilter: '',
    adminCatalogVisibleFilter: '',
    adminSalesChannelFilter: [],
    adminCreatedAtFilter: {
      startDate: '',
      endDate: '',
      appliedItem: '',
    },
    adminCatalogCategoryV2Filter: [],
  })

  const {
    handleNextPage,
    handlePrevPage,
    sortPagination,
    toggleOrder,
    initialSortValue,
  } = useSortPagination(ColumnsIDs.LAST_UPDATE)

  const searchState = useProductSearch()
  const columns = useProductListTableColumns()

  const filterQueries = useMemo(
    () => ({
      search:
        searchState.selectedItem?.id && searchState.selectedItem?.value
          ? new URLSearchParams(
              Object.values(searchState.selectedItem).join('=')
            ).toString()
          : '',
      hasActiveKit: filters.adminCatalogKitFilter
        ? `hasActiveKit=${filters.adminCatalogKitFilter}`
        : '',
      isActive: filters.adminCatalogStatusFilter
        ? `isActive=${filters.adminCatalogStatusFilter}`
        : '',
      isVisible: filters.adminCatalogVisibleFilter
        ? `isVisible=${filters.adminCatalogVisibleFilter}`
        : '',
      creationDateFrom: filters.adminCreatedAtFilter.startDate
        ? `CreationDateFrom=${filters.adminCreatedAtFilter.startDate}`
        : '',
      creationDateTo: filters.adminCreatedAtFilter.endDate
        ? `CreationDateTo=${filters.adminCreatedAtFilter.endDate}`
        : '',
    }),
    [searchState, filters]
  )

  const queries = useMemo(
    () => ({
      ...filterQueries,
      size: `size=${PAGE_SIZE}`,
      pagination: sortPagination,
    }),
    [filterQueries, sortPagination]
  )

  const bodyQueries = useMemo(
    () => ({
      CategoryIds: filters.adminCatalogCategoryV2Filter,
      BrandIds: filters.adminCatalogBrandFilter,
      SalesChannelIds: filters.adminSalesChannelFilter,
      SellerIds: filters.adminSellerFilter,
    }),
    [filters]
  )

  const hasPagination = useRef(false)

  const filtersEventProperties = useMemo(
    () =>
      transformProductListFiltersToEventProperties({
        brandFilter: filters.adminCatalogBrandFilter,
        categoryFilter: filters.adminCatalogCategoryV2Filter,
        createdAtFilter: filters.adminCreatedAtFilter,
        kitFilter: filters.adminCatalogKitFilter,
        salesChannelFilter: filters.adminSalesChannelFilter,
        sellerFilter: filters.adminSellerFilter,
        statusFilter: filters.adminCatalogStatusFilter,
        visibleFilter: filters.adminCatalogVisibleFilter,
        sortPagination,
        isPinned: searchState.pinnedSearchBy === searchState.selectedItem?.id,
      }),
    [
      filters,
      searchState.pinnedSearchBy,
      searchState.selectedItem?.id,
      sortPagination,
    ]
  )

  const {
    data: { products, total },
    loading,
    error,
    mutate,
    isLoadingOffers,
  } = useProducts(
    queries,
    bodyQueries,
    filtersEventProperties,
    hasPagination.current
  )

  const exportData = useMemo(
    () => ({ ...bodyQueries, filterQueries }),
    [bodyQueries, filterQueries]
  )
  const isFiltering = useMemo(
    () =>
      Object.keys(filterQueries)
        .filter((queryKey) => !!get(filterQueries, queryKey, ''))
        .concat(
          Object.keys(bodyQueries).filter(
            (queryKey) => get(bodyQueries, queryKey, []).length > 0
          )
        ).length > 0,
    [bodyQueries, filterQueries]
  )

  const pagination = usePaginationState({
    pageSize: PAGE_SIZE,
    total: total ?? 0,
  })

  hasPagination.current = pagination.currentPage > 1

  const view = useDataViewState({
    loading,
    notFound: false,
    error: null,
    empty: null,
  })

  const { getTable, getBodyCell, getHeadCell, data } = useTableState({
    length: PAGE_SIZE,
    items: products,
    columns,
    status: view.status,
    sort: {
      initialValue: initialSortValue,
      callback: (params: SortCallbackParams<Product>) => {
        toggleOrder(params)
        pagination.paginate({ type: 'navigate', page: 1 })
      },
    },
  })

  const updateViewStatus = useCallback(
    (status: DataViewStatus, action?: DataViewStatusAction) => {
      if (view.status === status) return

      return view.setStatus({ type: status, action })
    },
    [view]
  )

  useEffect(() => {
    if (error) {
      return updateViewStatus('error', {
        text: formatMessage({
          id: 'admin/admin.catalog.products-table-error-action',
          defaultMessage: 'Try again',
        }),
        onClick: mutate,
      })
    }

    const isSearching = searchState.selectedItem
    const activeFilters = Object.keys(filterQueries)
      .filter((queryKey) => !!get(filterQueries, queryKey, ''))
      .concat(
        Object.keys(bodyQueries).filter(
          (queryKey) => get(bodyQueries, queryKey, []).length > 0
        )
      )

    const isEmpty = !!products && products.length === 0
    const isFiltering = activeFilters.length > 0
    const isNotFound = isEmpty && (isFiltering || isSearching)

    if (loading) return updateViewStatus('loading')

    if (isNotFound) return updateViewStatus('not-found')

    if (isEmpty || !Array.isArray(products)) return updateViewStatus('empty')

    updateViewStatus('ready')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    products,
    updateViewStatus,
    error,
    filterQueries,
    bodyQueries,
    searchState.selectedItem,
    formatMessage,
    mutate,
  ])

  useEffect(() => {
    if (error) return

    const isTotalValid = typeof total === 'number' && total >= 0

    if (!isTotalValid || total === pagination.total) return

    pagination.paginate({ type: 'setTotal', total })

    const isFirstPage = pagination.currentPage === 1

    if (!isFirstPage) pagination.paginate({ type: 'navigate', page: 1 })
  }, [error, pagination, total])

  const paginate = (args: PaginationActionType) => {
    if (args.type === 'next') handleNextPage(products?.[products.length - 1])

    if (args.type === 'prev') handlePrevPage(products?.[0])

    pagination.paginate(args)
  }

  const productOffers = useMemo(() => {
    if (!products) return []

    return products
      .map((product) => product.offer)
      .filter((offer) => !!offer) as ProductOffer[]
  }, [products])

  const [downloadList, setDownloadList] = useLocalStorage(
    `exportDownloadList`,
    {}
  )

  const [hasFile, setHasFile] = useState<File | null>(null)
  const [importType, setImportType] = useState<ImportType>('Product')
  const { importResponse, importError, actualHash, postImportRequest } =
    useProductImport()
  return (
    <>
      <DataView state={view}>
        <DataViewHeader>
          <Flex justify="space-between" className={csx({ width: '100%' })}>
            <ProductSearch
              state={searchState}
              loading={loading && !!searchState.selectedItem}
            />
            <Flex align="center">
              <WithFeatureFlag featureFlagName="use_import_export">
                <ImportMenu
                  postImportRequest={postImportRequest}
                  hasFile={hasFile}
                  setHasFile={setHasFile}
                  importType={importType}
                  setImportType={setImportType}
                  importResponse={importResponse}
                />
                <ExportMenu
                  total={total}
                  exportData={exportData}
                  isFiltering={isFiltering}
                  onCreateExport={(newExport: DownloadItemProps) => {
                    setDownloadList(() => ({
                      ...newExport,
                    }))
                  }}
                />
                <DownloadListCollapser
                  items={downloadList}
                  onClear={() => setDownloadList({})}
                />
              </WithFeatureFlag>
              <Flex align="center" className={csx(style.pagination)}>
                <PaginationRangeLabel
                  from={pagination.range[0]}
                  to={pagination.range[1]}
                  total={pagination.total}
                />
                <Pagination
                  state={{
                    ...pagination,
                    paginate,
                  }}
                />
              </Flex>
            </Flex>
          </Flex>
          <FilterGroup
            onChangeStates={(data) => {
              setFilters(data as FilterGroupState)
            }}
          >
            <FilterStatus name="adminCatalogStatusFilter" />
            <FilterBrand name="adminCatalogBrandFilter" />
            <FilterCategory name="adminCatalogCategoryV2Filter" />
            <FilterSeller name="adminSellerFilter" />
            <FilterKit name="adminCatalogKitFilter" />
            <FilterVisible name="adminCatalogVisibleFilter" />
            <FilterSalesChannel name="adminSalesChannelFilter" />
            <FilterCreatedAt name="adminCreatedAtFilter" />
          </FilterGroup>
          <WithFeatureFlag featureFlagName="use_import_export">
            <ImportAlerts
              importResponse={importResponse}
              importError={importError}
              actualHash={actualHash}
            />
          </WithFeatureFlag>
        </DataViewHeader>
        <Table {...getTable()} className={csx(style.table)}>
          <THead>
            {columns.map((column) => (
              <THeadCell {...getHeadCell(column)} key={column.id} />
            ))}
          </THead>
          <ProductOffersProvider
            isLoadingOffers={isLoadingOffers}
            productOffers={productOffers}
          >
            <InactiveItemModalProvider>
              <ProductSKUsOffersModalProvider>
                <SKUListShortcutProvider>
                  {data.map((item) => (
                    <SkuListProvider product={item} key={item.id}>
                      <TBody className={csx(style.productRow)}>
                        <TBodyRow className={csx(style.productColumnsWrapper)}>
                          {columns.map((column) => (
                            <TBodyCell
                              {...getBodyCell(column, item)}
                              key={column.id}
                            />
                          ))}
                        </TBodyRow>
                        <SkuList />
                      </TBody>
                    </SkuListProvider>
                  ))}
                </SKUListShortcutProvider>
              </ProductSKUsOffersModalProvider>
            </InactiveItemModalProvider>
          </ProductOffersProvider>
        </Table>
        <Flex justify="flex-end" className={csx({ width: '100%' })}>
          <Flex align="center" className={csx(style.pagination)}>
            <PaginationRangeLabel
              from={pagination.range[0]}
              to={pagination.range[1]}
              total={pagination.total}
            />
            <Pagination
              state={{
                ...pagination,
                paginate,
              }}
            />
          </Flex>
        </Flex>
      </DataView>
    </>
  )
}
